import _ from 'lodash';
import { saveAs } from 'file-saver';
import { AudioSystem } from 'view/audioTracker/audio-system';

export default {
	computed: {
		mode() {
			return this.$route.query.mode;
		},
		isPublishMode() {
			return this.mode === 'publish' || this.$auth.isPublishMode();
		},
		publicationSession() {
			return this.$route.query.publicationSession;
		},
		publicationInProgress() {
			return this.publicationStatistic?.InProgress > 0 && this.publicationStatistic?.LastSession?.Status === 0;
		},
		publicationAvailable() {
			return this.publicationStatistic && !this.publicationStatistic.Published && !this.creativeIsLockedByOtherUser && !this.userRestrictedAccess;
		},
		publishButtonDisabled() {
			return !this.publicationStatistic
				|| (this.validationDueDate ? new Date(this.validationDueDate) > new Date() : false);
		},
		isGenerating() {
			return [1,2].indexOf(this.creative?.GenerationStatus) > -1;
		},
		isPublishing() {
			return !!this.publicationSession || this.isPublishingInitiated;
		},
		isCreativePublishHistoryVisible() {
			return this.dataIsLoadedFirst && (this.creative?.IsPublished || this.hasPublishHistory || (this.publicationStatistic && (this.publicationStatistic?.InProgress || this.publicationStatistic?.Published))) && !this.isPublishMode && !this.opts?.creative;
		}
	},
	data() {
		return {
			isPublished: null,
			publishPromisesResolvers: [],
			publishErrorPromisesResolvers: [],			
			hasPublishHistory: null,
			autoPublishInitiated: false,
			publicationStatistic: null,
			isPublishingInitiated: false,
			localPublicationStatistics: [],
			publicationLog: null,
		}
	},
	mounted() {
		this.$processPublicationSessionDebounced = _.debounce(() => this.checkProcessPublicationSessionUpdate(true), 2000, {
			leading: false,
			trailing: true
		});
		this.$eventsBus.$on('signalr-on-notification', this.publicationSignalrNotification);
	},
	destroyed() {
		delete this.$processPublicationSessionDebounced;
		this.$eventsBus.$off('signalr-on-notification', this.publicationSignalrNotification);

		if (this.$checkProcessPublicationSessionTimer) {
			clearTimeout(this.$checkProcessPublicationSessionTimer);
			delete this.$checkProcessPublicationSessionTimer;
		}
	},
	methods: {
		async showPublicationSessionLogs(session) {
			this.$set(session, '__isLoading', true);
			this.isLoading++;
			this.publicationLog = (await this.$Data.getExternalApi('PublicationSession/GetLogs', `publicationSessionId=${session.SessionId}`).finally(() => {
				this.isLoading--;
				this.$set(session, '__isLoading', false);
			}))?.Data;

			if (__DEBUG__)
				console.debug('showPublicationSessionLogs', { log: this.publicationLog });
		},
		publicationSignalrNotification({ eventName, data }) {
			if (eventName === 'CreativeVariantPrepared' && data?.SessionId === this.$publishSession?.SessionId) {
				this.$processPublicationSessionDebounced();
			}
		},
		checkPublicationStatisticProgress() {
			if (this.isPublishMode || this.opts?.creative) return;

			const check = async () => {
				const pubStat = await this.$Data.getExternalApi(
					'PublicationSession/GetPublicationStatistic',
					`creativeId=${this.creativeId}`
				);
				if (JSON.stringify(pubStat) !== JSON.stringify(this.publicationStatistic)) {
					this.publicationStatistic = pubStat;

					if (pubStat?.LastSession?.Status === 1 && !this.creative?.IsPublished) {
						if (this.$refs.publishHistory)
							this.$refs.publishHistory.reload();

						this.loadCreative();
					}
				}
			}

			check();
		},
		async downloadVariantsAsLinks(session) {
			this.isLoading++;
			try {
				let data = await this.$Data.postMethod(`externalApi?method=PublicationSession/DownloadUrls&parameters=${encodeURIComponent(`publicationSessionId=${session.SessionId}`)}`, {
					method: 'GET',
					xhr: () => {
						let xhr = new XMLHttpRequest();
						xhr.responseType = 'blob';
						return xhr;
					},
					processData: false,
					dataType: 'binary'
				});

				if (__DEBUG__)
					console.debug('Links', { data });

				let link = document.createElement('a');
				link.href = window.URL.createObjectURL(data);
				link.download = `${this.creative.Name} links.xlsx`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} finally {
				this.isLoading--;
			}
		},
		calcHasPublishHistory() {
			if (this.isPublishMode || this.opts?.creative) return;
			return this.$Data
				.getCreativePublishHistory(0, 1, {}, this.creativeId)
				.then(r => {
					this.hasPublishHistory = !!(r && r.Results && r.Results.length);
					return r;
				});
		},
		addToPublishLog(msg, additionalInfo, logType) {
			if (!this.$publishLog)
				this.$publishLog = [];
			let logMessage = `${this.$Data.getUtcDate()}: ${msg}. ${additionalInfo || ''}`;
			this.$publishLog.push(logMessage);
			this.$log(`PUBLISH: ${logMessage})`, { color: '#00CC00' });
			if (window.onLog) {
				window.onLog(msg, logType || 'processing', additionalInfo);
			}
		},
		checkProcessPublicationSessionUpdate(force) {
			if (force) {
				this.addToPublishLog(`Variant ready (signalR)`);
				this.processPublicationSession();
			} else {
				if (!this.$checkProcessPublicationSessionTimer) {
					this.addToPublishLog(`Waiting 15 seconds for next ready variants`);
					this.$checkProcessPublicationSessionTimer = setTimeout(() => {
						clearTimeout(this.$checkProcessPublicationSessionTimer);
						this.$checkProcessPublicationSessionTimer = null;
						this.processPublicationSession();
					}, 15000);
				}
			}
		},
		getMBFromSize(size) {
			return Math.floor(size / 1024 / 1024 * 100) / 100;
		},
		getAzureBlobStatistics(statistics) {
			return (statistics || this.$azure.getStatistics()).map(
				f => `${f.info?.cached ? '(CACHED)' : ''} ${f.info?.type} ${this.getMBFromSize(f.info?.size)} MB for ${f.info?.time} seconds, ${f.info?.operation}`)
		},
		async buildTracks(tracks) {
			const audioSystem = new AudioSystem();
			let trackSources = {};
			let trackSourcesData = {};
			let tracksBuffers = {};
			let trackEffectsPromises = {};
			
			tracks.map(f => tracksBuffers[f.id] = f.__trackBuffer);

			audioSystem.init({
				isTrial: this.$auth.isTrial(),
				trackSources,
				trackSourcesData,
				tracksBuffers,
				trackEffectsPromises,
				tracks,
				masterTrackConfig: this.creative.JsonConfig.masterTrackConfig,
				aggregatedTracks: this.$refs.tracker.aggregatedTracks
			});

			const build = await audioSystem.generateFinalAudio();

			if (__DEBUG__)
				console.debug('buildTracks'/*, { build }*/);

			tracks.map(f => f.__trackBuffer = null);

			return build;
		},
		checkEndSession(session) {
			this.addToPublishLog(`Check session ended (status = ${session?.Status})`);
			if (session?.Status !== 0) {
				if (session?.Status === 1) {
					this.finalizePublicationSession();
				} else {
					if (__DEBUG__)
						console.error('Publish: Publishing canceled');
					this.isPublished = false;
					this.isPublising = false;
					this.processPublishResolvers({ error: 'Publishing canceled' });
					this.$notifyError(this.$tCaption('Publish: Audio publishing has been stopped'));
				}
			}
		},
		async finalizeVariant(publishVariant, variant, finalAudioPromise) {
			try {
				this.addToPublishLog(`Finalize variant ${variant.Name}, waiting for audio upload`);
				if (__DEBUG__)
					console.debug(`FinalizeVariant ${variant.CreativeVariantId}`)
				variant.FinalAudio = await finalAudioPromise;
				variant.GenerationEnded = this.$Data.getUtcDate();
				variant.PublicationAttempt = this.$publishSession.PublicationAttempt;
				publishVariant.finalAudioPromise = finalAudioPromise;

				if (__DEBUG__)
					console.debug(`FinalizeVariant ${variant.CreativeVariantId}: FinalAudio saved`);

				publishVariant.addToPublishLog('Start ConfirmCreativeVariant');
				let session = await this.$Data.postExternalApi('PublicationSession/ConfirmCreativeVariant', variant);
				publishVariant.addToPublishLog(`Finished ConfirmCreativeVariant, status: ${session?.Status}`);
				this.checkEndSession(session);
			} catch (ex) {
				if (__DEBUG__) {
					console.error(ex);
					publishVariant.addToPublishLog(`Variant ${variant.Name} error: ${ex}`);
				}
				if (variant && ex) {
					this.$Data.postExternalApi('PublicationSession/LogCreativeVariant', {
						"CreativeVariantId": variant.CreativeVariantId,
						"IsError": true,
						"Message": `${ex}`
					});
				}
			}

			window.dispatchEvent(new CustomEvent('variantPublish', {
				detail: {
					variant,
					publishSession: this.$publishSession
				}
			}));

			if (window.onVariantPublish) {
				await window.onVariantPublish(variant, this.$publishSession);
			}

			if (__DEBUG__)
				console.debug('Publish: Insertions loaded for variant', { variant });

			let azureStatistics = publishVariant.azureStatistics || [];
			let variantStatistics = publishVariant.variantStatistics;

			variantStatistics.blobsStatistiscs.push(...this.getAzureBlobStatistics(azureStatistics));
			variantStatistics.totalTime = this.$moment().diff(variantStatistics.variantProcessingStarted, 'seconds');

			let downloaded = azureStatistics.filter(f => f.info?.type === 'DOWNLOAD');
			let uploaded = azureStatistics.filter(f => f.info?.type === 'UPLOAD');

			variantStatistics.totalDownloaded = this.getMBFromSize(downloaded.reduce((res, f) => res + (f.info?.cached ? 0 : f.info?.size), 0));
			variantStatistics.totalDownloadedTime = downloaded.reduce((res, f) => res + f.info?.time, 0);
			variantStatistics.totalCached = this.getMBFromSize(downloaded.reduce((res, f) => res + (f.info?.cached ? f.info?.size : 0), 0));
			variantStatistics.totalUploaded = this.getMBFromSize(uploaded.reduce((res, f) => res + f.info?.size || 0, 0));
			variantStatistics.totalUploadedTime = uploaded.reduce((res, f) => res + f.info?.time, 0);

			publishVariant.addToPublishLog(`${variant.Name} statistics: DOWN ${variantStatistics.totalDownloaded}mb for ${variantStatistics.totalDownloadedTime}s,` 
				+ ` UPL ${variantStatistics.totalUploaded}mb for ${variantStatistics.totalUploadedTime}s, CACHE ${variantStatistics.totalCached}mb`);

			publishVariant.addToPublishLog(`PublicationSession/LogCreativeVariant`);
			this.$Data.postExternalApi('PublicationSession/LogCreativeVariant', {
				"CreativeVariantId": variant.CreativeVariantId,
				"IsError": false,
				"Message": JSON.stringify(variantStatistics, void 0, '  ')
			});
			publishVariant.addToPublishLog(`PublicationSession/LogCreativeVariant loaded`);

			variantStatistics.azureStatistics = azureStatistics;
			this.localPublicationStatistics.push({
				variant,
				variantStatistics 
			});

			publishVariant.blobProcessed = true;
			publishVariant.addToPublishLog(`Blob processed`);
		},
		async processNextPublicationVariants() {
			let limit = parseInt(this.$route?.query?.maxSimultaneousVariants) || 10;
			let processingCount = (this.$publishingVariants || []).filter(f => !f.processed && f.isProcessing).length;
			let waiting = (this.$publishingVariants || []).filter(f => !f.processed && !f.isProcessing);
			let adding = waiting.slice(0, Math.max(limit - processingCount, 0));

			adding.map(f => this.processNextPublicationVariant(f));

			this.checkProcessPublicationSessionUpdate();

			this.addToPublishLog(`ProcessNextPublicationVariants: limit ${limit}, processing: ${processingCount}, waiting: ${waiting?.length}, added: ${adding?.length}`);
		},
		async processNextPublicationVariant(publishVariant) {
			const variant = publishVariant?.variant;

			this.addToPublishLog(`ProcessNextPublicationVariant: ${publishVariant?.variant?.Name || 'no variants available'}`);

			if (!variant) {
				return;
			}

			let variantProcessingStarted = new Date();
			let variantStatistics = {
				variantProcessingStarted,
				blobsStatistiscs: [],
				mixingTime: 0,
				totalTime: 0,
				log: []
			}

			const addToPublishLog = (v, additionalInfo, logType) => {
				variantStatistics.log.push(`${this.$Data.getUtcDate()}: ${v}`);
				this.addToPublishLog(v, additionalInfo || `(${variant?.Name})`, logType);
			}

			publishVariant.variantStatistics = variantStatistics;
			publishVariant.addToPublishLog = addToPublishLog;
			publishVariant.isProcessing = true;

			try {
				if (__DEBUG__)
					console.debug(`Processing variant`, { variant });

				addToPublishLog(`Processing variant`);

				this.isLoading++;
				variant.GenerationStarted = this.$Data.getUtcDate();

				addToPublishLog(`PublicationSession/GetCreativeVariantInsertions`);
				const basedOnInsertions = await this.$Data.getExternalApi('PublicationSession/GetCreativeVariantInsertions', `creativeVariantId=${variant.CreativeVariantId}`);
				addToPublishLog(`PublicationSession/GetCreativeVariantInsertions loaded`);

				addToPublishLog(`GetCreativeVariantInsertions: ${basedOnInsertions?.length} items`);
				if (!Array.isArray(basedOnInsertions)) {
					throw('PublicationSession/GetCreativeVariantInsertions returned non-array data: ', JSON.stringify(basedOnInsertions));
				}

				publishVariant.basedOnInsertions = basedOnInsertions;

				let onProgress = (ev, newTrack) => {
					addToPublishLog(`${parseInt(100 * ev.loaded / ev.total)}% audio downloaded for track "${(newTrack.name() || '').substr(0, 20) || newTrack.id}"`, void 0, 'network');
				}

				let tracks = [];
				let createTrackByInsertion = (ins, mediaFile) => {
					let newTrack = this.getTrackFromInsertion(ins);

					//To resovle links
					newTrack.__tracker = {
						tracks
					}

					if (__DEBUG__)
						console.debug('Publish: Loaded new track'/*, newTrack, ins*/);
					if (this.$refs.tracker)
						this.$refs.tracker.initSystemTrackInfo(newTrack);

					newTrack.src = newTrack?.__src || ((asBlob) => this.$azure.getInsertionAudio(newTrack.insertion, asBlob, (ev) => onProgress(ev, newTrack), void 0, variant.CreativeVariantId));

					if (!newTrack.__aggregatedTrack.muted) {
						newTrack.insertion.Audio = mediaFile;
						tracks.push(newTrack);
					}

					return newTrack;
				}

				addToPublishLog(`Load publish insertions`);
				addToPublishLog(`Creative/GetInsertionByIds`);
				let insertions = await this.$Data.postExternalApi('Creative/GetInsertionByIds', basedOnInsertions.map(f => f.InsertionId));
				addToPublishLog(`Creative/GetInsertionByIds loaded`);
				addToPublishLog(`Loaded ${insertions?.length}`);

				addToPublishLog(`Load parent insertions (to resolve links)`);
				let parents = insertions.filter(f => f.ParentId).map(f => f.ParentId);
				if (parents.length) {
					addToPublishLog(`Creative/GetInsertionByIds`);
					let parentInsertions = await this.$Data.postExternalApi('Creative/GetInsertionByIds', parents);
					addToPublishLog(`Creative/GetInsertionByIds loaded`);
					parentInsertions.map(basedInsertion => {
						let newTrack = createTrackByInsertion(basedInsertion);
						newTrack.__allowPublishing = false;
					});
				}
				addToPublishLog(`Loaded ${parents?.length}`);

				basedOnInsertions.map(basedInsertion => {
					let newTrack = createTrackByInsertion(insertions.find(f => f.Oid === basedInsertion.InsertionId), basedInsertion.MediaFile);
					newTrack.__allowPublishing = true;
				});

				addToPublishLog(`Download audio from azure`);
				this.$refs.tracker.tracksBuffers = {};
				if (tracks.length) {
					await Promise.all(tracks.map(async track => {
						if (track.__allowPublishing) {
							var response = await this.$refs.tracker.getSrc(track.src, track);
							var arrayBuffer = await response.data.arrayBuffer();
							addToPublishLog(`Array buffer parsed: ${!!arrayBuffer}`);
							
							var buffer = await this.$refs.tracker.audioContext.decodeAudioData(arrayBuffer);
							addToPublishLog(`Audio decoded: ${!!buffer}`);

							if (this.isSonicScribe) {
								addToPublishLog(`Build SonicScribe audio`);
								buffer = await this.buildSonicScribe(this.creative, track, buffer);
								addToPublishLog(`SonicScribe audio done`);
							}

							buffer = await this.$refs.tracker.preProcessBuffer(buffer, track, track?.__aggregatedTrack);
							await this.$refs.tracker.processLoadedAudioBufferFixes(track, buffer);
							addToPublishLog(`Audio prepared for track`);

							Object.defineProperty(track, '__trackBuffer', {
								get: () => buffer,
								set: (v) => buffer = v
							});
						}
						track.__isSrcLoaded = true;
					}));
				}
				await this.$refs.tracker.loadData(tracks).catch((ex) => {
					addToPublishLog(`ERROR while downloading audio: ${ex}`);
				});
				addToPublishLog(`Audio loaded`);

				addToPublishLog(`Resolve sub-insertions/parent relation links`);
				const resolved = tracks.length && (await Promise.all(tracks.map(async f => {
					if (f.__isSubTrack) {
						let parent = tracks.find(t => t.id === f.parentId);
						let __activeSubTrackDeltaWidth = f.getTrimmedDuration() - parent.getTrimmedDuration() - (f?.insertion?.JsonConfig?.parentStartDelta || 0);
						if (__activeSubTrackDeltaWidth) {
							await parent.__processLinkedTracks({ delta: __activeSubTrackDeltaWidth, changed: 'end', type: 'resolveSubInsertions', deltaWidth: __activeSubTrackDeltaWidth }, true);
						}
						return 1;
					}
					return 0;
				}))).reduce((res,v) => res += v, 0);
				addToPublishLog(`Resolved ${resolved} sub-insertions/parent relation links`);
				
				let mixingStarted = new Date();
				const audioBlob = await new Promise((resolve, reject) => {
					addToPublishLog(`Start mixing audio`);
					this.buildTracks(tracks).then(r => {
						if (__DEBUG__)
							console.debug('Publish: Final audio result'/*, { r }*/);
						if (r.wav) {
							let fr = new FileReader;
							fr.onloadend = () => {
								addToPublishLog(`Audio mixed`);
								resolve(fr.result)
							}
							fr.readAsDataURL(r.wav);
						} else {
							addToPublishLog(`Audio NOT mixed`);
							resolve(null);
							console.error('Publish: No audio has been generated');
						}
					}).catch(ex => reject(ex));
					tracks = null;
					return null;
				}).catch(ex => {
					this.$Data.postExternalApi('PublicationSession/LogCreativeVariant', {
						"CreativeVariantId": variant.CreativeVariantId,
						"IsError": true,
						"Message": `Audio not mixed: ${ex}`
					});
				});
				variantStatistics.mixingTime = this.$moment().diff(mixingStarted, 'seconds');

				if (audioBlob) {
					publishVariant.blobProcessed = false;

					addToPublishLog(`Start uploading final audio for ${variant.Name} (${audioBlob?.size} bytes)`);
					const creativeUploadInfo = await this.$azure.uploadCreativeFinalAudio(this.creative, audioBlob, `${variant.CreativeVariantId}`, true, true, variant.CreativeVariantId, (ev) => {
						addToPublishLog(`${ev?.percent}% upload final audio for ${variant.Name}`);
					});
					if (__DEBUG__)
						console.debug('creativeUploadInfo', { creativeUploadInfo });
					this.finalizeVariant(publishVariant, variant, creativeUploadInfo?.upload);
				} else {
					publishVariant.blobProcessed = true;
					this.$Data.postExternalApi('PublicationSession/LogCreativeVariant', {
						"CreativeVariantId": variant.CreativeVariantId,
						"IsError": true,
						"Message": `Audio not mixed`
					});
				}
			} catch(ex) {
				if (__DEBUG__) {
					console.error(ex);
					addToPublishLog(`Variant ${variant.Name} error: ${ex}`);
				}
				if (variant && ex) {
					this.$Data.postExternalApi('PublicationSession/LogCreativeVariant', {
						"CreativeVariantId": variant.CreativeVariantId,
						"IsError": true,
						"Message": `${ex}`
					});
				}
			} finally {
				setImmediate(() => {
					publishVariant.azureStatistics = this.$azure.getStatistics().filter(f => f.info?.logId === publishVariant.variant.CreativeVariantId);

					this.isLoading--;

					if (publishVariant) {
						publishVariant.processed = true;
						publishVariant.isProcessing = false;
					}

					if (!this.$publishSession?.Status) {
						//this.processNextPublicationVariants();
					}
				});
			}
		},
		async finalizePublicationSession() {
			if (this.isPublished) return;

			this.isPublished = true;

			if (__DEBUG__)
					console.debug('Publish: Publish variants processed');

			if (__DEBUG__)
				console.debug('Publish: End publish session'/*, { publishSession: this.$publishSession }*/);

			this.$publishSession.GenerationEnded = this.$Data.getUtcDate();

			this.addToPublishLog(`PublicationSession/FinalizePublicationSession`);
			const finalize = await this.$Data.getExternalApi('PublicationSession/FinalizePublicationSession', `publicationSessionId=${this.$publishSession?.SessionId}`);
			this.addToPublishLog(`PublicationSession/FinalizePublicationSession loaded, success: ${finalize?.Success || finalize?.ErrorMessage}`);

			this.$notifySuccess(this.$tCaption('Publish: Audio has been successfully published'));

			this.processPublishResolvers();

			this.isPublishingInitiated = false;
		},
		processPublicationSession() {
			const orderParam = this.$route?.query?.order !== void 0 ? `&order=${this.$route.query.order}` : '';
			const publicationSessionParam = `publicationSession=${this.publicationSession || this.$publishingSessionId}${orderParam}`;

			this.addToPublishLog(`PublicationSession/GetPublicationSessionForPublishing`);
			this.$Data.getExternalApi('PublicationSession/GetPublicationSessionForPublishing', publicationSessionParam).then(async (publishSession) => {
				this.addToPublishLog(`PublicationSession/GetPublicationSessionForPublishing loaded`);

				this.checkEndSession(publishSession);

				if (!this.$publishSession) {
					this.$publishSession = publishSession;

					if (__DEBUG__)
						console.debug('Publish: Start publish session'/*, { publishSession }*/);

					this.addToPublishLog(`Start session ${publishSession.SessionId}`);
					this.$publishSession.GenerationStarted = this.$Data.getUtcDate();
				} else {
					this.$publishSession.Status = publishSession.Status;
					if (__DEBUG__)
						console.debug('Publish: checking ready variants'/*, { publishSession }*/);

					this.addToPublishLog(`Checking ready variants ${publishSession.SessionId}`);
				}

				let addedItems = 0;
				(publishSession.CreativeVariants || []).map(f => {
					if (!this.$publishingVariants.find(v => v?.variant?.CreativeVariantId === f.CreativeVariantId)) {
						this.$publishingVariants.push({
							variant: f,
							processed: false
						});
						this.addToPublishLog(`Found ready variant (${f.Name}, ${f.CreativeVariantId})`);
						addedItems++;
					}
				});

				if (__DEBUG__)
					console.debug(`%cPublish: generating ${publishSession.CreativeVariants?.length} variants`,  'background: yellow; color: black');

				this.addToPublishLog(`Added to queue new ${addedItems} variants`);

				this.processNextPublicationVariants();
			}).catch(ex => {
				this.addToPublishLog(`Exception: ${ex}`);
				console.error(ex);
				this.$notifyError(ex);
				this.isPublished = false;
				this.processPublishResolvers({ error: ex });
			});
		},
		publishFinalAudio(immediate, sessionId) {
			if (this.isPublishingInitiated) return;
			const publish = () => {
				this.isLoading++;
				this.isPublished = null;
				this.isPublishingInitiated = true;
				this.$publishLog = [];
				this.$publishingSessionId = sessionId;
				this.$publishingVariants = [];
				this.$publishSession = null;
				this.localPublicationStatistics = [];
				this.processPublicationSession();
			};
			if (immediate) {
				publish();
			} else {
				this.$confirmMessage({
					title: 'Confirm action',
					text: 'Are you sure you want to publish final audio?',
					handler: () => {
						publish();
					}
				});
			}
		},
		cancelPublishFinalAudioServerside(publicationSessionId) {
			this.$confirmMessage({
				title: 'Confirm action',
				text: 'Are you sure you want to cancel publishing final audio?',
				handler: () => {
					this.publicationStatistic = { InProgress: 0, LastSession: { Status: 3 } };
					this.$Data
						.getExternalApi(
							'PublicationSession/CancelPublicationSession',
							`publicationSessionId=${publicationSessionId}`
						)
						.finally(() => {
							this.calcHasPublishHistory();
							this.loadCreative();
							if (this.$refs.publishHistory)
								this.$refs.publishHistory.reload();
							this.$notifySuccess(this.$tCaption('Publishing canceled'));
						});
				}
			});
		},
		onPublishFinalAudioServerside(retryPublicationSessionId, file) {
			this.publicationStatistic = { InProgress: 1, LastSession: { Status: 0 } };
		},
		onPublishFinalAudioServersideInitialized() {
			this.checkPublicationStatisticProgress();
			this.calcHasPublishHistory();
			this.loadCreative();

			if (this.$refs.publishHistory)
				this.$refs.publishHistory.reload();
		},
		onPublish() {
			if (this.isPublishingInitiated && this.isPublished !== null) {
				this.addToPublishLog(`onPublish: publishing already finished, return results`);
				return Promise.resolve({
					isPublished: this.isPublished,
					log: this.$publishLog
				});
			}

			this.addToPublishLog(`onPublish: subscribed`);
			return new Promise((resolve) => {
				this.publishPromisesResolvers.push(resolve);
			});
		},
		processPublishResolvers(data) {
			this.addToPublishLog(`Process onPublish ${this.isPublished}`);
			const result = {
				isPublished: this.isPublished,
				...(data || {}),
				log: this.$publishLog
			};

			this.publishPromisesResolvers.map(f => {
				f(result);
			});

			if (window.onPublish) {
				window.onPublish(result);
			}

			if (__DEBUG__)
				console.debug('%cPublish: published true', 'background: green; color: white', result);
			this.publishPromisesResolvers = [];
		},
	}
};
